<template>
  <div
    class="box box-padding bg-gradient-to-r relative logo-background"
    :class="boxColor"
  >
    <div class="relative z-20">
      <h2 class="text-white font-semibold text-lg md:text-xl lg:text-2xl">
        {{ title }}
      </h2>
      <p
        class="text-white md:text-md lg:text-lg mt-10"
        :class="{ 'mb-20': !hasMultipleRoles }"
      >
        {{ lead }}
      </p>
      <ul
        v-if="hasMultipleRoles"
        class="text-white md:text-md lg:text-lg mb-20 list-disc pl-20 mt-10"
      >
        <li v-for="role in requiredRoleLabels" :key="role">{{ role }}</li>
      </ul>

      <div class="flex gap-10">
        <ButtonStyle v-slot="{ classes }" v-bind="buttonAttributes" inverted>
          <button :class="classes" @click.prevent="() => paywallAction()">
            {{ buttonText }}
          </button>
        </ButtonStyle>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ButtonStyleProps } from '~/components/ButtonStyle/index.vue'
import { useDrupalUser, DrupalRole } from '~/stores/drupalUser'

const route = useRoute()
const router = useRouter()
const drupalUser = useDrupalUser()
const language = useCurrentLanguage()
const { $texts } = useNuxtApp()

const buttonText = computed<string>(() => {
  return drupalUser.isLoggedIn
    ? $texts('loginWithAnotherAccount', 'Mit anderem Konto anmelden')
    : $texts('register', 'Anmelden')
})

const props = withDefaults(
  defineProps<{
    showPaywall?: boolean
    requiredRoles?: string[]
    destination?: string
  }>(),
  {
    requiredRoles: () => [],
    destination: '',
  },
)

const { roleLabels } = useDrupalRoleHelper()

const hasMultipleRoles = computed<boolean>(() => props.requiredRoles.length > 1)

const requiredRoleLabels = computed<string[]>(() => {
  return props.requiredRoles.map(
    (role) => roleLabels.value[role as keyof typeof roleLabels.value] || role,
  )
})

const targetRole = computed<string>(() => {
  return props.requiredRoles[0] || ''
})

const computedDestination = computed<string>(() => {
  // When the paywall is used for media we show the paywall on a dedicated
  // route (as there is no media canonical) and so pass the destination in the
  // props. If we are showing the paywall on a node page we use the current
  // route path as the destination to redirect to after login.
  return props.destination || route.path
})

const hasPharmacyRole = computed<boolean>(() => {
  return (
    props.requiredRoles.includes(DrupalRole.AffiliatedPharmacy) ||
    props.requiredRoles.includes(DrupalRole.NonAffiliatedPharmacy)
  )
})

const hasMemberRole = computed<boolean>(() => {
  return props.requiredRoles.includes(DrupalRole.IndividualMember)
})

const boxColor = computed<string>(() => {
  if (hasPharmacyRole.value && hasMemberRole.value) {
    return 'from-green to-green-alt'
  }

  if (hasPharmacyRole.value) {
    return 'from-berry to-pink'
  }

  return 'from-orange-light to-orange'
})

const buttonAttributes = computed<ButtonStyleProps>(() => {
  if (hasPharmacyRole.value && hasMemberRole.value) {
    return { green: true }
  }
  if (hasPharmacyRole.value) {
    return { berry: true }
  }
  return { orange: true }
})

const title = computed<string>(() => {
  // For multiple roles, use generic title.
  if (hasMultipleRoles.value) {
    return $texts('paywallContent.TitleGeneric', 'Geschützte Inhalte')
  }

  // Single role: show role-specific title.
  if (props.requiredRoles.includes(DrupalRole.NonAffiliatedPharmacy)) {
    return $texts(
      'paywallContent.TitleNonAffiliatedPharmacy',
      'Informationen für nicht angeschlossene Apotheken.',
    )
  }
  if (props.requiredRoles.includes(DrupalRole.AffiliatedPharmacy)) {
    return $texts(
      'paywallContent.TitlePharmacy',
      'Informationen für angeschlossene Apotheken.',
    )
  }
  return $texts(
    'paywallContent.TitleMember',
    'Informationen für Einzelmitglieder.',
  )
})

const lead = computed<string>(() => {
  // For multiple roles, use the generic text.
  if (hasMultipleRoles.value) {
    return $texts(
      'paywallContent.TextMultipleRoles',
      'Um auf diesen Inhalt zugreifen zu können, melden Sie sich bitte an als:',
    )
  }

  // Single role: show role-specific text.
  if (props.requiredRoles.includes(DrupalRole.NonAffiliatedPharmacy)) {
    return $texts(
      'paywallContent.TextNonAffiliatedPharmacy',
      'Bitte melden Sie sich als nicht angeschlossene Apotheke an, um auf diesen Inhalt zuzugreifen.',
    )
  }

  if (props.requiredRoles.includes(DrupalRole.AffiliatedPharmacy)) {
    return $texts(
      'paywallContent.TextPharmacy',
      'Bitte melden Sie sich als angeschlossene Apotheke an, um auf diesen Inhalt zuzugreifen.',
    )
  }

  return $texts(
    'paywallContent.TextMember',
    'Bitte melden Sie sich als Einzelmitglied an, um auf diesen Inhalt zuzugreifen.',
  )
})

async function paywallAction() {
  // We need to redirect to the login page with the relevant role
  // and the destination. If the user is logged in we first need to log them
  // out.
  const query: { role?: string; destination: string } = {
    destination: computedDestination.value,
  }

  // Only include role param for single role, omit for multiple roles.
  if (!hasMultipleRoles.value && targetRole.value) {
    query.role = targetRole.value
  }

  if (drupalUser.isLoggedIn) {
    // We need to first logout the user (we don't redirect at this point).
    const destination = router.resolve({
      path: `/${language.value}/user/login`,
      query,
    })
    // We redirect the user to the destination after logging them out.
    await drupalUser.logout(destination.href)
  } else {
    // Redirect to the login page with the relevant role (if applicable)
    // and the destination.
    router.push({
      name: 'login',
      query,
    })
  }
}
</script>
